import { useEffect, useRef, useState } from 'react';
import { Stack } from '@rabex-kit/core';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@rabex-kit/rabex-ui';
import OTP from '../../components/Login/OTP';
import { CHANGE_PASSWORD, LOGIN } from '../../pages/routes';
import {
  useLoginOTPMutation,
  useLoginResendOTPMutation,
  usePasswordOTPMutation,
  usePasswordOTPVerifyMutation,
  useRegisterOTPMutation,
  useRegisterResendOTPMutation
} from '../../services/auth';
import MockUp from '../../static/images/svg/auth/TOTP.svg';
import DarkMockUp from '../../static/images/svg/auth/TOTP-dark.svg';
import TokenManager, { TokenType } from '../../utils/tokenManager';
import handleSubmitError from '../../utils/authErrors';
import useFinishSignIn from '@/hooks/useFinishSignIn';

const OtpContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { palette, breakpoints } = useTheme();
  // Below lg the art is not rendered at all, so phones never download it.
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const finishSignIn = useFinishSignIn();

  const [loginResendOTPRequest] = useLoginResendOTPMutation();
  const [registerResendOTP] = useRegisterResendOTPMutation();
  const [forgetResendOTP] = usePasswordOTPMutation();
  const [
    loginOTPRequest,
    { isLoading: isLoginLoading, isSuccess: isLoginSuccess }
  ] = useLoginOTPMutation();
  const [
    registerOTPRequest,
    { isLoading: isRegisterLoading, isSuccess: isRegisterSuccess }
  ] = useRegisterOTPMutation();
  const [
    passwordOTPVerifyRequest,
    { isLoading: isPasswordLoading, isSuccess: isPasswordSuccess }
  ] = usePasswordOTPVerifyMutation();

  // Set when the reset code is accepted: the change-password page still needs the pending token.
  const keepPendingToken = useRef(false);
  // Bumped on a rejected code so the OTP boxes empty and refocus for the next try.
  const [clearSignal, setClearSignal] = useState(0);
  // Leaving the code step any other way abandons the flow, so its pending token goes with it.
  useEffect(
    () => () => {
      if (!keepPendingToken.current) TokenManager.clearPending();
    },
    []
  );

  const from = location?.state?.from || '';

  if (!from) {
    return <Navigate to={LOGIN} replace />;
  }
  /** True once the code was sent again; a failure's message is toasted by rtkQueryErrorLogger. */
  const handleResendCode = async (): Promise<boolean> => {
    try {
      if (from === 'register') {
        await registerResendOTP().unwrap();
      }
      if (from === 'login') {
        await loginResendOTPRequest().unwrap();
      }
      if (from === 'forget') {
        const res = await forgetResendOTP({
          phone_number: location?.state?.username || ''
        }).unwrap();
        // Resending the reset code issues a new reset token; the next verify must carry it.
        TokenManager.setPending(res.data.accessToken);
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleOnSubmit = (code: string) => {
    const type = location?.state?.type || 'sms';
    if (from === 'login') {
      loginOTPRequest({
        sms_code: type === 'sms' ? code : '',
        email_code: type === 'email' ? code : '',
        ga_code: type === 'ga' ? code : ''
      })
        .unwrap()
        .then((res) => {
          handleSuccessLogin(res.data);
        })
        .catch(handleWrongCode);
    }
    if (from === 'register') {
      registerOTPRequest({ code })
        .unwrap()
        .then((res) => {
          handleSuccessLogin(res.data);
        })
        .catch(handleWrongCode);
    }
    if (from === 'forget') {
      passwordOTPVerifyRequest({ code })
        .unwrap()
        .then((res) => {
          // Authorises only the password change, so it stays pending rather than becoming a session.
          TokenManager.setPending(res.data.accessToken);
          keepPendingToken.current = true;
          navigate(CHANGE_PASSWORD);
        })
        .catch(handleWrongCode);
    }
  };

  function handleWrongCode(error: { status?: unknown }) {
    handleSubmitError(error);
    setClearSignal((count) => count + 1);
  }

  const handleSuccessLogin = (tokens: TokenType) =>
    finishSignIn(
      tokens,
      from === 'register' ? 'registration' : 'login',
      location.state?.returnTo
    );

  return (
    <Stack flexDirection="row" gap={17.5}>
      <OTP
        onResendCode={handleResendCode}
        clearSignal={clearSignal}
        onSubmit={handleOnSubmit}
        isLoading={
          isPasswordLoading ||
          isPasswordSuccess ||
          isLoginLoading ||
          isLoginSuccess ||
          isRegisterLoading ||
          isRegisterSuccess
        }
      />
      {!isMobile && (
        <Stack
          sx={{
            zIndex: 1,
            maxWidth: 656,
            position: 'relative',
            width: 656,
            height: 613,
            overflow: 'visible',
            top: '-115px'
          }}>
          <img
            src={palette.mode === 'light' ? MockUp : DarkMockUp}
            alt="mockup"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -56%)'
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default OtpContainer;
